import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import DatePickerField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/DatePickerField';
import NumberInputField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/NumberInputField';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import ClassesStudentsSelectDropdown from './ClassesStudentsSelectDropdown';
import { FIELD_META_MAP } from 'chameleon/src/components/assign/AssignManageForm/utils/constants';
import { FIELD_NAMES } from './BasicSettings';
import { useStartDueDateFields } from './hooks';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

const CasSetting = ({
  id,
  scrambleQuestionsPerStudent,
  reviewModeFlag,
  isPreApSubject,
  isSPP,
  isMCQPPC,
  individualStudentAssignmentFlag,
  assignments,
  studentsBySection,
  router,
  settingNumber,
  otherActiveSettings,
  deleteCasSetting,
  isAssessmentForm,
  assignableSections,
  disabledStudents,
  timezone,
  allowLateSubmissionFF,
  allowHardTimer,
}) => {
  useEffect(() => {
    headingRef?.current && headingRef.current.focus();
  }, []);

  const headingRef = useRef(null);
  const { setValue, getValues, watch, formState } = useFormContext();
  const timer = watch(`${id}.${FIELD_NAMES.TIMER}`);
  const dueAt = watch(`${id}.${FIELD_NAMES.DUE_AT}`);
  const selectedAssignees = watch(`${id}.${FIELD_NAMES.CLASS_DROPDOWN}`);
  const noAssigneesSelected = isEmpty(Object.values(selectedAssignees).flat());

  const {
    startDateMaxDate,
    startDateMinDate,
    formatedStartsAt,
    dueAtMinDate,
    formatedDueAt,
  } = useStartDueDateFields({
    id,
    timezone,
    startsAtName: FIELD_NAMES.STARTS_AT,
    dueAtName: FIELD_NAMES.DUE_AT,
  });
  const errors = get(formState.errors, id, {});

  const assignmentInfo = {
    isPreApSubject,
    isResourceForm: false,
    isSPP,
    isAssign: true,
    hasPreassignedBehavior: false,
    individualStudentAssignmentFlag,
    isMCQPPC,
    featureFlags: {
      scrambleQuestionsPerStudent,
      reviewModeFlag,
      allowLateSubmission: allowLateSubmissionFF,
    },
  };

  const handleValueChange = (field, value) => {
    setValue(`${id}.${field}`, value, { shouldValidate: true });
  };

  const headingId = `${id}-heading`;
  const heading = `Assignment Settings ${settingNumber}`;
  const closeButton = `${id}-close-button`;

  return (
    <div role="group" aria-labelledby={headingId}>
      <div className="flex w-full justify-between">
        <h2
          ref={headingRef}
          id={headingId}
          tabIndex="-1"
          className="mb-6 text-default font-normal m-0"
        >
          {heading}
        </h2>
        <span id={closeButton} className="sr-only">{`Remove ${heading}`}</span>
        <ButtonWithIcon
          className="delete-custom-setting"
          icon="close"
          iconWidth={24}
          iconHeight={24}
          onClick={() => deleteCasSetting({ settingsId: id })}
          size="tiny"
          isIcon
          isNaked
          aria-labelledby={closeButton}
          iconAccessibility={{
            ariaHidden: true,
          }}
        />
      </div>
      <div className="flex gap-x-6">
        <div className="flex-1">
          <ClassesStudentsSelectDropdown
            classes={assignableSections || []}
            data-test-id={`${id}-sections`}
            title={FIELD_META_MAP.SECTIONS.title(assignmentInfo)}
            infoPopupText={FIELD_META_MAP.SECTIONS.helpText(assignmentInfo)}
            selectedValue={getValues(FIELD_NAMES.CLASS_DROPDOWN)}
            onValueChange={(value) =>
              handleValueChange(FIELD_NAMES.CLASS_DROPDOWN, value)
            }
            allAssigneesName="allAssignees"
            id={id}
            disabledStudents={disabledStudents}
            inProgressAssignments={assignments}
            studentsBySection={studentsBySection}
            isAssessmentForm={isAssessmentForm}
            router={router}
            isCASDropdown
            otherActiveSettings={otherActiveSettings}
          />
          {!isSPP && (
            <NumberInputField
              name={`${id}.${FIELD_NAMES.TIMER}`}
              data-test-id={`${id}-${FIELD_NAMES.TIMER}`}
              title={FIELD_META_MAP.TIMER.title()}
              infoPopupText={FIELD_META_MAP.TIMER.helpText({ allowHardTimer })}
              icon={<Icon id="timer" accessibility={{ ariaHidden: true }} />}
              description={timer > 1 ? 'mins' : 'min'}
              disabled={noAssigneesSelected}
            >
              {!!timer && (
                <div
                  className="text-base leading-relaxed text-gray-600"
                  role="region"
                  aria-live="polite"
                >
                  {FIELD_META_MAP.TIMER.detailsComponent({ allowHardTimer })}
                </div>
              )}
            </NumberInputField>
          )}
        </div>
        <div className="flex-1">
          <DatePickerField
            name={`${id}.${FIELD_NAMES.STARTS_AT}`}
            data-test-id={`${id}-${FIELD_NAMES.STARTS_AT}`}
            title={FIELD_META_MAP.START_DATE.title(assignmentInfo)}
            infoPopupText={FIELD_META_MAP.START_DATE.helpText(assignmentInfo)}
            error={
              errors[FIELD_NAMES.STARTS_AT] && {
                message: errors[FIELD_NAMES.STARTS_AT].message,
              }
            }
            onReset={() => handleValueChange(FIELD_NAMES.STARTS_AT, null)}
            maxDate={startDateMaxDate}
            minDate={startDateMinDate}
            initialValue={formatedStartsAt}
            disabled={noAssigneesSelected}
            timezone={timezone}
          />
          <DatePickerField
            name={`${id}.${FIELD_NAMES.DUE_AT}`}
            data-test-id={`${id}-${FIELD_NAMES.DUE_AT}`}
            title={FIELD_META_MAP.DUE_DATE.title(assignmentInfo)}
            infoPopupText={FIELD_META_MAP.DUE_DATE.helpText(assignmentInfo)}
            error={
              errors[FIELD_NAMES.DUE_AT] && {
                message: errors[FIELD_NAMES.DUE_AT].message,
              }
            }
            onReset={() => handleValueChange(FIELD_NAMES.DUE_AT, null)}
            minDate={dueAtMinDate}
            initialValue={formatedDueAt}
            disabled={noAssigneesSelected}
            timezone={timezone}
          >
            {dueAt && (
              <div
                className="text-base leading-relaxed text-gray-600"
                role="region"
                aria-live="polite"
              >
                {FIELD_META_MAP.DUE_DATE.detailsComponent({
                  allowLateSubmission: allowLateSubmissionFF,
                })}
              </div>
            )}
          </DatePickerField>
        </div>
      </div>
    </div>
  );
};

export default CasSetting;
